import { useMemo } from 'react';

type TokenKind = 'key' | 'string' | 'number' | 'literal' | 'punctuation' | 'space';

interface Token {
  kind: TokenKind;
  text: string;
}

// Tessra's badge hues: they are the palette's named colours, and they carry their own dark-mode
// values, which a fixed syntax theme would not.

const TONE: Record<TokenKind, string> = {
  key: 'text-badge-blue-foreground',
  string: 'text-badge-lime-foreground',
  number: 'text-badge-orange-foreground',
  literal: 'text-badge-purple-foreground',
  punctuation: 'text-muted-foreground',
  space: '',
};

const TOKEN =
  /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([{}[\],:])|(\s+)/g;

/** Splits pretty-printed JSON into the pieces it is coloured by. A string followed by a colon is a key. */
export function tokenizeJson(text: string): Token[] {
  const tokens: Token[] = [];
  for (const match of text.matchAll(TOKEN)) {
    const [, string, colon, number, literal, punctuation, space] = match as unknown as Array<
      string | undefined
    >;
    if (string !== undefined) {
      tokens.push({ kind: colon ? 'key' : 'string', text: string });
      if (colon) tokens.push({ kind: 'punctuation', text: colon });
    } else if (number !== undefined) tokens.push({ kind: 'number', text: number });
    else if (literal !== undefined) tokens.push({ kind: 'literal', text: literal });
    else if (punctuation !== undefined) tokens.push({ kind: 'punctuation', text: punctuation });
    else if (space !== undefined) tokens.push({ kind: 'space', text: space });
  }
  return tokens;
}

export interface JsonViewProps {
  value: unknown;
  className?: string;
}

/** A value shown as the JSON it is, indented and coloured by token. */
export function JsonView({ value, className }: JsonViewProps) {
  const tokens = useMemo(
    () => tokenizeJson((JSON.stringify(value, null, 2) as string | undefined) ?? String(value)),
    [value],
  );
  return (
    <pre
      className={`m-0 overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] font-mono text-xs leading-relaxed ${className ?? ''}`}
    >
      <code>
        {tokens.map((token, index) => (
          <span key={index} className={TONE[token.kind]}>
            {token.text}
          </span>
        ))}
      </code>
    </pre>
  );
}
